import type React from "react";
import { useState, useEffect } from "react";
import { Lock, CheckCircle, AlertCircle, ArrowLeft, KeyRound } from "lucide-react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "../../../../services/axiosInstance";

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!message && !error) return;

    const t = setTimeout(() => {
      setMessage("");
      setError("");
    }, 5000);

    return () => clearTimeout(t);
  }, [message, error]);

  const validatePassword = (password: string) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoading) return;

    setMessage("");
    setError("");

    if (!token) {
      setError("Token invalide ou manquant");
      return;
    }

    if (!password || !confirmPassword) {
      setError("Tous les champs sont obligatoires");
      return;
    }

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    if (!validatePassword(password)) {
      setError(
        "Mot de passe faible (min 8 caractères, majuscule, minuscule, chiffre, symbole)"
      );
      return;
    }

    setIsLoading(true);

    try {
      const res = await axios.post("/api/users/reset-password", {
        token,
        password,
        confirmPassword,
      });

      setMessage(res.data?.message || "Mot de passe réinitialisé avec succès");

      setTimeout(() => {
        navigate("/auth");
      }, 2000);
    } catch (err: any) {
      setError(
        err?.response?.data?.error || "Erreur lors de la réinitialisation"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
       <div
        className="pointer-events-none fixed inset-0 opacity-[0.03]"  
      />
      <div
        className={`w-full max-w-[420px] transition-all duration-500 ease-out
        ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
      >
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_32px_rgba(0,0,0,0.08)] overflow-hidden">

          <div className="h-1 w-full bg-gradient-to-r from-[#0F52BA] via-blue-400 to-blue-200" />

          <div className="px-8 py-8">

            <div className="flex flex-col items-center text-center mb-8">
              <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-[#0F52BA] mb-4 shadow-sm">
                <KeyRound size={22} />
              </div>

              <h1 className="text-xl font-bold text-slate-800 tracking-tight mb-1">
                Réinitialiser le mot de passe
              </h1>

              <p className="text-sm text-slate-400 max-w-[260px]">
                Choisissez un nouveau mot de passe sécurisé pour votre compte.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">

              <div>
                <label className="text-xs font-semibold text-slate-600">
                  Nouveau mot de passe
                </label>

                <div className="relative mt-1">
                  <Lock
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300"
                  />

                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50/60
                    text-sm focus:outline-none focus:border-[#0F52BA]
                    focus:ring-2 focus:ring-blue-100 focus:bg-white transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">
                  Confirmer le mot de passe
                </label>

                <div className="relative mt-1">
                  <Lock
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300"
                  />

                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50/60
                    text-sm focus:outline-none focus:border-[#0F52BA]
                    focus:ring-2 focus:ring-blue-100 focus:bg-white transition-all"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl
                bg-[#0F52BA] text-white text-sm font-bold
                hover:bg-blue-700 active:scale-[0.99]
                disabled:opacity-60 disabled:cursor-not-allowed transition"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Réinitialisation...
                  </>
                ) : (
                  <>
                    <KeyRound size={14} />
                    Réinitialiser
                  </>
                )}
              </button>
            </form>
            {message && (
              <div className="mt-4 flex items-start gap-2.5 px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-700 text-sm">
                <CheckCircle size={16} />
                {message}
              </div>
            )}

            {error && (
              <div className="mt-4 flex items-start gap-2.5 px-4 py-3 bg-rose-50 border border-rose-100 rounded-xl text-rose-600 text-sm">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <div className="mt-6 pt-5 border-t border-slate-100 flex justify-center">
              <Link
                to="/auth"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#0F52BA]"
              >
                <ArrowLeft size={13} />
                Retour à la connexion
              </Link>
            </div>
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-400 mt-4">
          Utilisez un mot de passe fort pour sécuriser votre compte.
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;